import React, { useState } from 'react';
import { Calendar, Clock, Search, Filter, Mail, CheckCircle, Send } from 'lucide-react';

const NurseAppointments = () => {
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sendingReminder, setSendingReminder] = useState(null);

  const mockAppointments = [
    {
      id: '1',
      date: '2026-01-30',
      time: '09:00 AM',
      patient: 'Mrs. Ngozi Okeke',
      patientEmail: 'ngozi.okeke@email.com',
      doctor: 'Dr. Sarah Johnson',
      type: 'Antenatal Check-up',
      status: 'upcoming',
      reminderSent: false,
    },
    {
      id: '2',
      date: '2026-01-30',
      time: '10:30 AM',
      patient: 'Mrs. Fatima Bello',
      patientEmail: 'fatima.bello@email.com',
      doctor: 'Dr. Michael Chen',
      type: 'Postnatal Follow-up',
      status: 'upcoming',
      reminderSent: true,
    },
    {
      id: '3',
      date: '2026-01-30',
      time: '02:00 PM',
      patient: 'Mrs. Adaobi Nwankwo',
      patientEmail: 'adaobi.nwankwo@email.com',
      doctor: 'Dr. Sarah Johnson',
      type: 'Pregnancy Consultation',
      status: 'upcoming',
      reminderSent: false,
    },
    {
      id: '4',
      date: '2026-01-31',
      time: '09:00 AM',
      patient: 'Mrs. Aisha Mohammed',
      patientEmail: 'aisha.mohammed@email.com',
      doctor: 'Dr. Michael Chen',
      type: 'Child Health Review',
      status: 'scheduled',
      reminderSent: false,
    },
    {
      id: '5',
      date: '2026-01-29',
      time: '03:00 PM',
      patient: 'Mrs. Sarah Williams',
      patientEmail: 'sarah.williams@email.com',
      doctor: 'Dr. Sarah Johnson',
      type: 'General Consultation',
      status: 'completed',
      reminderSent: true,
    },
    {
      id: '6',
      date: '2026-01-29',
      time: '11:00 AM',
      patient: 'Mrs. Grace Obi',
      patientEmail: 'grace.obi@email.com',
      doctor: 'Dr. Michael Chen',
      type: 'Antenatal Check-up',
      status: 'cancelled',
      reminderSent: false,
    },
  ];

  const [appointments, setAppointments] = useState(mockAppointments);

  const getStatusBadge = (status) => {
    const badges = {
      upcoming: 'bg-blue-100 text-blue-700',
      scheduled: 'bg-purple-100 text-purple-700',
      completed: 'bg-green-100 text-green-700',
      cancelled: 'bg-red-100 text-red-700',
    };
    return badges[status] || 'bg-gray-100 text-gray-700';
  };

  const handleSendReminder = async (appointmentId) => {
    setSendingReminder(appointmentId);
    
    // Simulate sending email
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    setAppointments(prev => 
      prev.map(apt => 
        apt.id === appointmentId 
          ? { ...apt, reminderSent: true }
          : apt
      )
    );
    
    setSendingReminder(null);
    alert('Email reminder sent successfully!');
  };

  const handleSendAllReminders = async () => {
    const upcomingAppointments = appointments.filter(
      apt => (apt.status === 'upcoming' || apt.status === 'scheduled') && !apt.reminderSent
    );

    if (upcomingAppointments.length === 0) {
      alert('No pending reminders to send!');
      return;
    }

    if (!window.confirm(`Send reminders to ${upcomingAppointments.length} patient(s)?`)) {
      return;
    }

    setSendingReminder('all');
    
    // Simulate sending all emails
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    setAppointments(prev => 
      prev.map(apt => 
        (apt.status === 'upcoming' || apt.status === 'scheduled')
          ? { ...apt, reminderSent: true }
          : apt
      )
    );
    
    setSendingReminder(null);
    alert(`Successfully sent ${upcomingAppointments.length} email reminder(s)!`);
  };

  const filteredAppointments = appointments.filter(apt => {
    const matchesSearch = 
      apt.patient.toLowerCase().includes(searchTerm.toLowerCase()) ||
      apt.doctor.toLowerCase().includes(searchTerm.toLowerCase()) ||
      apt.type.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === 'all' || apt.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  const appointmentsByDate = filteredAppointments.reduce((acc, apt) => {
    if (!acc[apt.date]) {
      acc[apt.date] = [];
    }
    acc[apt.date].push(apt);
    return acc;
  }, {});

  const upcomingCount = appointments.filter(apt => 
    (apt.status === 'upcoming' || apt.status === 'scheduled') && !apt.reminderSent
  ).length;

  return (
    <div className="p-6 md:p-8 min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl shadow-lg shadow-emerald-500/30">
                <Calendar className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-3xl md:text-4xl font-bold text-gray-900">
                  Appointments
                </h1>
                <p className="text-gray-600 mt-1">View and manage all appointments</p>
              </div>
            </div>
            
            {upcomingCount > 0 && (
              <button
                onClick={handleSendAllReminders}
                disabled={sendingReminder === 'all'}
                className="px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-blue-600 transition-all shadow-lg shadow-blue-500/30 flex items-center gap-2 disabled:opacity-50"
              >
                {sendingReminder === 'all' ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    Send All Reminders ({upcomingCount})
                  </>
                )}
              </button>
            )}
          </div>
        </div>

        {/* Filters & Search */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by patient, doctor, or type..."
                className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              />
            </div>

            {/* Status Filter */}
            <div className="flex items-center gap-2">
              <Filter className="w-5 h-5 text-gray-500" />
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              >
                <option value="all">All Status</option>
                <option value="upcoming">Upcoming</option>
                <option value="scheduled">Scheduled</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
          </div>
        </div>

        {/* Appointments List */}
        <div className="space-y-6">
          {Object.keys(appointmentsByDate).length > 0 ? (
            Object.keys(appointmentsByDate).sort().reverse().map(date => (
              <div key={date}>
                <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Clock className="w-5 h-5 text-emerald-600" />
                  {new Date(date).toLocaleDateString('en-US', { 
                    weekday: 'long', 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </h2>
                <div className="grid gap-4">
                  {appointmentsByDate[date].map(appointment => (
                    <div
                      key={appointment.id}
                      className="bg-white rounded-xl shadow-lg border border-gray-100 p-6 hover:shadow-xl transition-shadow"
                    >
                      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-start gap-4">
                            <div className="p-3 bg-emerald-50 rounded-xl">
                              <Calendar className="w-6 h-6 text-emerald-600" />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <h3 className="text-lg font-bold text-gray-900">
                                  {appointment.patient}
                                </h3>
                                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusBadge(appointment.status)}`}>
                                  {appointment.status}
                                </span>
                                {appointment.reminderSent && (
                                  <span className="px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700 flex items-center gap-1">
                                    <CheckCircle className="w-3 h-3" />
                                    Reminder Sent
                                  </span>
                                )}
                              </div>
                              <p className="text-gray-600 mb-1">{appointment.type}</p>
                              <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                                <span className="flex items-center gap-1">
                                  <Clock className="w-4 h-4" />
                                  {appointment.time}
                                </span>
                                <span>Doctor: {appointment.doctor}</span>
                                <span className="flex items-center gap-1">
                                  <Mail className="w-4 h-4" />
                                  {appointment.patientEmail}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="flex gap-2">
                          {(appointment.status === 'upcoming' || appointment.status === 'scheduled') && !appointment.reminderSent && (
                            <button
                              onClick={() => handleSendReminder(appointment.id)}
                              disabled={sendingReminder === appointment.id}
                              className="px-6 py-3 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition-all flex items-center gap-2 disabled:opacity-50"
                            >
                              {sendingReminder === appointment.id ? (
                                <>
                                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                  Sending...
                                </>
                              ) : (
                                <>
                                  <Mail className="w-5 h-5" />
                                  Send Reminder
                                </>
                              )}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))
          ) : (
            <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-12 text-center">
              <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-2">No appointments found</h3>
              <p className="text-gray-600">Try adjusting your search or filter criteria</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NurseAppointments;
